import React from 'react';
import AppImage from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CartDrawer = ({ isOpen, onClose, cartItems, onUpdateQuantity, onRemoveItem, onCheckout }) => {
  const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) || 0;
  const FREE_SHIPPING_THRESHOLD = 2500;
  const SHIPPING_COST = 149.90;
  const shippingFee = (subtotal > 0 && subtotal < FREE_SHIPPING_THRESHOLD) ? SHIPPING_COST : 0;
  const total = subtotal + shippingFee;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Slide-Over Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[450px] bg-white z-50 shadow-xl transform transition-transform duration-300 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
          <h2 className="text-xl font-bold text-gray-900">
            Sepetiniz ({cartItems?.length || 0} {cartItems?.length === 1 ? 'ürün' : 'ürün'})
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Sepeti kapat"
          >
            <Icon name="X" size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Empty State */}
        {cartItems?.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Icon name="ShoppingCart" size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sepetiniz şu an boş.</h3>
            <p className="text-sm text-gray-600 mb-6">
              Alışverişe başlamak için ürün ekleyin
            </p>
            <Button variant="default" onClick={onClose}>
              Alışverişe Başla
            </Button>
          </div>
        ) : (
          <>
            {/* Scrollable Body - Cart Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {cartItems?.map((item) => (
                  <div 
                    key={item?.cartItemId || item?.id} 
                    className={`flex gap-4 pb-4 border-b border-gray-200 last:border-0 ${item?.bundle_instance_id ? 'bg-orange-50/40 rounded-xl px-2 pt-2 -mx-2' : ''}`}
                  >
                    {/* Product Image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      {item?.image ? (
                        <AppImage
                          src={item?.image}
                          alt={item?.imageAlt || `${item?.title} product image`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon name="Package" size={24} className="text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        {item?.bundle_instance_id && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-100 rounded-full px-2 py-0.5 mb-1">
                            📦 Set Ürünü
                          </span>
                        )}
                        <h4 className="font-bold text-sm text-gray-900 line-clamp-2 mb-1">
                          {item?.title}
                        </h4>
                        {(item?.selectedColor || item?.selectedSize) && (
                          <p className="text-xs text-gray-500 mb-1">
                            {item?.selectedColor && `${item?.selectedColor}`}
                            {item?.selectedColor && item?.selectedSize && ' / '}
                            {item?.selectedSize && `${item?.selectedSize}`}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">
                            {item?.price?.toLocaleString('tr-TR')}₺
                          </p>
                          {item?.bundle_instance_id && item?.original_price && item?.original_price > item?.price && (
                            <p className="text-xs text-gray-400 line-through">
                              {item?.original_price?.toLocaleString('tr-TR')}₺
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => onUpdateQuantity(item?.cartItemId || item?.id, -1)}
                          className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                          aria-label="Miktarı azalt"
                        >
                          <Icon name="Minus" size={16} className="text-gray-600" />
                        </button>
                        <span className="text-sm font-medium text-gray-900 min-w-[2rem] text-center">
                          {item?.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(item?.cartItemId || item?.id, 1)}
                          className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-300 hover:bg-gray-50 transition-colors"
                          aria-label="Miktarı artır"
                        >
                          <Icon name="Plus" size={16} className="text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Delete Button */}
                    <div className="flex flex-col items-center gap-1 self-start">
                      <button
                        onClick={() => onRemoveItem(item?.cartItemId || item?.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 text-red-600 transition-colors"
                        aria-label="Kaldır"
                        title={item?.bundle_instance_id ? 'Bu ürünü kaldırırsanız diğer set ürünleri normal fiyata döner' : 'Kaldır'}
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fixed Footer - Subtotal & Checkout */}
            <div className="border-t border-gray-200 bg-white px-6 py-4 space-y-4">
              {/* Shipping Progress */}
              {subtotal > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  {subtotal < FREE_SHIPPING_THRESHOLD ? (
                    <>
                      <p className="text-sm font-semibold text-green-700 mb-1.5">
                        🚚 Kargo bedavaya <span className="font-bold">₺{(FREE_SHIPPING_THRESHOLD - subtotal)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> kaldı!
                      </p>
                      <div className="w-full bg-green-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100)}%` }}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm font-semibold text-green-700">🎉 Tebrikler! Kargonuz Bedava.</p>
                  )}
                </div>
              )}

              {/* Subtotal */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Ara Toplam</span>
                  <span className="font-semibold text-gray-900">
                    {subtotal?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Kargo Ücreti</span>
                  <span className={`font-semibold ${shippingFee === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                    {shippingFee === 0 ? 'Ücretsiz' : '₺149,90'}
                  </span>
                </div>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-base font-bold text-gray-900">Toplam</span>
                <span className="text-2xl font-bold text-gray-900">
                  {total?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₺
                </span>
              </div>

              {/* Checkout Button */}
              <Button
                variant="default"
                fullWidth
                onClick={onCheckout}
                size="lg"
                className="h-14 text-lg font-semibold flex items-center justify-center gap-2"
              >
                <Icon name="CreditCard" size={20} />
                <span>Siparişi Tamamla</span>
              </Button>

              {/* Continue Shopping Link */}
              <button
                onClick={onClose}
                className="w-full text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-1 transition-colors"
              >
                ← Alışverişe Devam Et
              </button>

              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-4 text-xs text-gray-500 pt-2">
                <div className="flex items-center gap-1">
                  <Icon name="Shield" size={14} />
                  <span>Güvenli Ödeme</span>
                </div>
                <div className="flex items-center gap-1">
                  <Icon name="Truck" size={14} />
                  <span>Hızlı Teslimat</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartDrawer;